/**
 * Magic Link Verification Page
 * Handles passwordless login via magic link from email
 *
 * @requirement BRD-000 Foundational Screens
 * @screen SCR-001 Login (Magic Link variant)
 */

import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { useAuth } from '@myvault/shared/hooks';
import { Alert, Spinner } from '@myvault/shared/components';

const API_BASE = '/api/v1/auth';

type VerificationState = 'verifying' | 'success' | 'error' | 'used' | 'expired';

export function MagicLinkPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { setTokensAndRefresh } = useAuth();
  const [state, setState] = useState<VerificationState>('verifying');
  const [errorMessage, setErrorMessage] = useState<string>('');

  const token = searchParams.get('token');

  const verifyMutation = useMutation({
    mutationFn: async (magicToken: string) => {
      const response = await fetch(`${API_BASE}/magic-link/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: magicToken }),
        credentials: 'include',
      });

      const data = await response.json();

      if (!response.ok) {
        // Determine error type
        if (data.error_code === 'AUTH_MAGIC_LINK_USED') {
          throw new Error('USED');
        } else if (data.error_code === 'AUTH_INVALID_MAGIC_LINK') {
          throw new Error('EXPIRED');
        }
        throw new Error(data.detail || 'Verificatie mislukt');
      }

      return data;
    },
    onSuccess: async (data) => {
      // Store tokens and refresh user data
      // API returns camelCase due to Pydantic alias_generator
      const accessToken = data.accessToken || data.access_token;
      const refreshToken = data.refreshToken || data.refresh_token;

      if (!accessToken || !refreshToken) {
        console.error('Magic link response missing tokens:', Object.keys(data));
        setState('error');
        setErrorMessage('Geen geldige tokens ontvangen van de server');
        return;
      }

      try {
        // Store tokens and refresh user - this also triggers onLoginRedirect
        await setTokensAndRefresh(accessToken, refreshToken);
        setState('success');
        // Note: setTokensAndRefresh calls onLoginRedirect which navigates to /dashboard
        // The redirect happens via window.location.replace, so no additional navigation needed
      } catch (err) {
        console.error('Failed to set tokens and refresh user:', err);
        setState('error');
        setErrorMessage('Inloggen mislukt - kon gebruikersgegevens niet ophalen');
      }
    },
    onError: (err: Error) => {
      if (err.message === 'USED') {
        setState('used');
        setErrorMessage('Deze inloglink is al gebruikt. Vraag een nieuwe link aan.');
      } else if (err.message === 'EXPIRED') {
        setState('expired');
        setErrorMessage('Deze inloglink is verlopen. Vraag een nieuwe link aan.');
      } else {
        setState('error');
        setErrorMessage(err.message);
      }
    },
  });

  useEffect(() => {
    if (token) {
      verifyMutation.mutate(token);
    } else {
      setState('error');
      setErrorMessage('Geen token gevonden in de URL');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const handleRetry = () => {
    navigate('/login');
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4">
      <div className="w-full max-w-md">
        <div className="rounded-lg bg-white p-8 shadow-lg">
          {/* Logo */}
          <div className="mb-8 text-center">
            <h1 className="text-2xl font-bold text-mv-blue-900">myVault</h1>
          </div>

          {/* Verifying State */}
          {state === 'verifying' && (
            <div className="text-center">
              <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-blue-100">
                <Spinner size="lg" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900">Bezig met inloggen...</h2>
              <p className="mt-2 text-gray-600">
                Even geduld terwijl we je inloglink verifiëren.
              </p>
            </div>
          )}

          {/* Success State */}
          {state === 'success' && (
            <div className="text-center">
              <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-green-100">
                <svg
                  className="h-8 w-8 text-green-600"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>
              <h2 className="text-xl font-semibold text-gray-900">Succesvol ingelogd!</h2>
              <p className="mt-2 text-gray-600">
                Je wordt doorgestuurd naar je dashboard...
              </p>
            </div>
          )}

          {/* Error States */}
          {(state === 'error' || state === 'used' || state === 'expired') && (
            <div className="text-center">
              <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-red-100">
                <svg
                  className="h-8 w-8 text-red-600"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </div>
              <h2 className="text-xl font-semibold text-gray-900">
                {state === 'used' && 'Link al gebruikt'}
                {state === 'expired' && 'Link verlopen'}
                {state === 'error' && 'Inloggen mislukt'}
              </h2>

              <Alert variant="error" className="mt-4">
                {errorMessage}
              </Alert>

              <button
                onClick={handleRetry}
                className="mt-6 w-full rounded-lg bg-mv-blue-600 py-3 font-semibold text-white transition-colors hover:bg-mv-blue-800"
              >
                Terug naar inloggen
              </button>
            </div>
          )}
        </div>

        {/* Help text */}
        <p className="mt-4 text-center text-sm text-gray-500">
          Problemen met inloggen?{' '}
          <a href="/help" className="text-mv-blue-600 hover:underline">
            Neem contact op met support
          </a>
        </p>
      </div>
    </div>
  );
}

export default MagicLinkPage;
